/**
 * 数字人 IM WebSocket 客户端（应用层协议 + 自动重连 + 回执匹配）
 */
import { ImMessageVO } from '@src/services/teammate';

// ─── 协议枚举与载荷 ─────────────────────────────────────────────────────────
import { v4 } from 'uuid';
export enum MessageType {
  Message = 'message',
  Ack = 'ack',
  System = 'system',
  Heartbeat = 'heartbeat',
}

export enum ActionType {
  Send = 'send',
  Broadcast = 'broadcast',
  Success = 'success',
  Error = 'error',
  Notify = 'notify',
  UserRemoved = 'userRemoved',
  ChannelDeleted = 'channelDeleted',
  AgentAdd = 'agentAdd',
  UserAdd = 'userAdd',
  AgentRemoved = 'agentRemoved',
}
export enum EventType {
  START = 'start',
  TOKEN = 'token',
  ERROR = 'error',
  TASK_COMPLETED = 'task_completed',
}

export interface WsEnvelopeData extends ImMessageVO {
  threadId?: string;
  eventType?: EventType;
  removedUserIds?: string[];
  errorCode?: string;
  errorMsg?: string;
  agentNameMap?: Record<string, string>;
  userNameMap?: Record<string, string>;
  addedUserIds?: string[];
  operatorUserId?: string;
  marioAgentId?: string;
}

export interface WsEnvelope {
  type: MessageType;
  action: ActionType;
  requestId: string;
  data: WsEnvelopeData;
}

// ─── 配置 ───────────────────────────────────────────────────────────────────

export interface ImWsClientOptions {
  /** WebSocket URL */
  url: string;
  /** 子协议，透传至浏览器 WebSocket 构造函数 */
  protocols?: string | string[];
  /** 是否断线自动重连，默认 true */
  autoReconnect?: boolean;
  /**
   * 重连最大次数（单次 connect 生命周期内累计）。
   * 与 ClawWsClient 一致：大于 0 时达到次数后停止；为 0 表示不限制次数。
   */
  maxReconnectAttempts?: number;
  /** 重连基础延迟（ms），指数退避底数，默认 1000 */
  reconnectBaseDelayMs?: number;
  /** 重连最大延迟（ms），默认 30_000 */
  reconnectMaxDelayMs?: number;
  /** 在退避时间上额外叠加的随机抖动上界（ms），含 0～该值之间的均匀随机，默认 500 */
  reconnectJitterMs?: number;
  /** 带 ACK 的发送默认超时（ms），默认 30_000 */
  defaultAckTimeoutMs?: number;
  /**
   * 心跳间隔（ms）。连接成功后定时发送 `MessageType.Heartbeat`，
   * 服务端以 `Ack` 确认；默认 20_000。
   */
  heartbeatIntervalMs?: number;
}

type MessageHandler = (envelope: WsEnvelope) => void;
type SystemHandler = (envelope: WsEnvelope) => void;

type PendingAck = {
  resolve: (envelope: WsEnvelope) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
};

const DEFAULT_OPTS: Required<Omit<ImWsClientOptions, 'url' | 'protocols'>> = {
  autoReconnect: true,
  maxReconnectAttempts: 10,
  reconnectBaseDelayMs: 1000,
  reconnectMaxDelayMs: 30_000,
  reconnectJitterMs: 500,
  defaultAckTimeoutMs: 30_000,
  heartbeatIntervalMs: 20_000,
};

const genRequestId = () => v4();

// ─── 错误 ───────────────────────────────────────────────────────────────────

export class ImWsAckError extends Error {
  constructor(
    message: string,
    public readonly envelope: WsEnvelope,
    public readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'ImWsAckError';
  }
}

/** 某次发送在约定时间内未收到 ACK，客户端将关闭连接并自动重连（若开启 autoReconnect） */
export class ImWsAckTimeoutError extends Error {
  constructor(
    message: string,
    public readonly requestId: string,
  ) {
    super(message);
    this.name = 'ImWsAckTimeoutError';
  }
}

type AckErrorHandler = (err: ImWsAckError) => void;

// ─── 客户端 ─────────────────────────────────────────────────────────────────

export class ImWsClient {
  private readonly opts: Required<
    Omit<ImWsClientOptions, 'url' | 'protocols'>
  > &
    Pick<ImWsClientOptions, 'url' | 'protocols'>;

  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private manualClose = false;
  private connectPromise: Promise<void> | null = null;

  private pendingAck = new Map<string, PendingAck>();
  private messageHandlers = new Set<MessageHandler>();
  private systemHandlers = new Set<SystemHandler>();
  private ackErrorHandlers = new Set<AckErrorHandler>();

  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatAckInFlight = false;
  private cacheBoardList = new Set<string>();

  constructor(options: ImWsClientOptions) {
    this.opts = {
      ...DEFAULT_OPTS,
      ...options,
    };
  }

  /** 当前底层 WebSocket 状态（无连接时为 CLOSED） */
  get readyState(): number {
    return this.ws?.readyState ?? WebSocket.CLOSED;
  }

  /** 是否已打开 */
  get isOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * 建立连接；已连接时立即 resolve。
   * 重连成功也会走同一套 onopen，不会重复挂多个 connect Promise（并发 connect 共享同一个 Promise）。
   */
  connect(): Promise<void> {
    if (this.isOpen) return Promise.resolve();
    if (this.connectPromise) return this.connectPromise;

    this.manualClose = false;
    this.connectPromise = new Promise((resolve, reject) => {
      try {
        const ws = new WebSocket(this.opts.url);
        this.ws = ws;

        ws.onopen = () => {
          this.reconnectAttempts = 0;
          this.connectPromise = null;
          // this.startHeartbeat();
          resolve();
        };

        ws.onmessage = (ev) => {
          this.handleIncoming(ev);
        };

        ws.onerror = (ev) => {
          console.log('【ImWsClient】ws.onerror', ev);
          this.connectPromise = null;
          reject(ev);
        };

        ws.onclose = () => {
          console.log('【ImWsClient】ws.onclose');
          this.connectPromise = null;
          this.stopHeartbeat();
          this.rejectAllPending(new Error('[ImWs] Connection closed'));
          if (!this.manualClose) {
            this.scheduleReconnect();
          }
        };
      } catch (e) {
        this.connectPromise = null;
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    });

    return this.connectPromise;
  }

  /**
   * 主动关闭，不再自动重连。
   */
  disconnect(code = 1000, reason = 'client disconnect'): void {
    this.manualClose = true;
    this.rejectAllPending(new Error('[ImWs] Client disconnected'));
    this.stopHeartbeat();
    this.cacheBoardList.clear();
    if (this.ws) {
      this.ws.close(code, reason);
      this.ws = null;
    }
  }

  /**
   * 注册业务消息回调（服务端主动下发的 `message`，典型为 `action: broadcast`）。
   * 返回取消订阅函数。
   */
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => this.messageHandlers.delete(handler);
  }

  /**
   * 注册系统消息回调（服务端主动下发的 `system:notify`）。
   */
  onSystem(handler: SystemHandler): () => void {
    this.systemHandlers.add(handler);
    return () => this.systemHandlers.delete(handler);
  }

  /**
   * 服务端 ACK 且 `action: error`（业务/逻辑失败）。便于页面统一展示 Toast 等，与 `send*` 的 Promise reject 并行触发。
   */
  onAckError(handler: AckErrorHandler): () => void {
    this.ackErrorHandlers.add(handler);
    return () => this.ackErrorHandlers.delete(handler);
  }

  /**
   * 发送聊天消息（`MessageType.Message`）。服务端以 `Ack` 回执，`ack.data.message_id` 与本次 `request_id` 一致。
   * `data.message_id` 可选；不传则与本次 `request_id` 相同写入载荷，便于服务端落库。
   */
  sendMessage(
    data: Partial<WsEnvelopeData> = {},
    options?: { timeoutMs?: number },
  ): Promise<WsEnvelope> {
    const rid = genRequestId();
    this.cacheBoardList.add(rid);
    const envelope: WsEnvelope = {
      type: MessageType.Message,
      action: ActionType.Send,
      requestId: rid,
      data: {
        ...data,
        messageId: data.messageId ?? rid,
      },
    };
    return this.sendEnvelope(envelope, options?.timeoutMs);
  }

  /**
   * 发送心跳（`MessageType.Heartbeat`），同样通过 `Ack` 确认。
   */
  private _sendHeartbeat(
    data: Partial<WsEnvelopeData> = {},
    options?: { timeoutMs?: number },
  ): Promise<WsEnvelope> {
    const rid = genRequestId();
    const payload: WsEnvelopeData = {
      messageId: rid,
      ...data,
    };
    return this.sendEnvelope(
      {
        type: MessageType.Heartbeat,
        action: ActionType.Send,
        requestId: rid,
        data: payload,
      },
      options?.timeoutMs,
    );
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    if (this.opts.heartbeatIntervalMs <= 0) return;
    this.heartbeatAckInFlight = false;

    // 连接刚建立就先发一次，避免服务端/链路在长空闲后直接断开
    this.safeSendHeartbeat();
    this.heartbeatTimer = setInterval(
      () => this.safeSendHeartbeat(),
      this.opts.heartbeatIntervalMs,
    );
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
    this.heartbeatAckInFlight = false;
  }

  private safeSendHeartbeat(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    // 避免心跳 ACK 尚未返回时并发堆积
    if (this.heartbeatAckInFlight) return;
    this.heartbeatAckInFlight = true;
    const timeoutMs = Math.max(
      1000,
      Math.min(
        this.opts.defaultAckTimeoutMs,
        // 让心跳 ACK 超时小于心跳间隔，避免“Promise 不 settle → 只发一次”
        Math.max(1000, this.opts.heartbeatIntervalMs - 1000),
      ),
    );
    this._sendHeartbeat({}, { timeoutMs })
      .catch(() => {
        // sendEnvelope 会在 ACK timeout 时触发 closeForAckTimeout，从而进入 onclose 自动重连
        // 这里吞掉错误，避免 interval 引起未处理 Promise rejection
      })
      .finally(() => {
        this.heartbeatAckInFlight = false;
      });
  }

  private sendEnvelope(
    envelope: WsEnvelope,
    timeoutMs?: number,
  ): Promise<WsEnvelope> {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error('[ImWs] WebSocket is not open'));
    }
    const requestId = envelope.requestId;
    const timeout = timeoutMs ?? this.opts.defaultAckTimeoutMs;

    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingAck.delete(requestId);
        const err = new ImWsAckTimeoutError(
          `[ImWs] ACK timeout: ${requestId}`,
          requestId,
        );
        console.log('ack timeout', err);
        reject(err);
        this.closeForAckTimeout();
      }, timeout);

      this.pendingAck.set(requestId, {
        resolve: (ack) => {
          clearTimeout(timer);
          resolve(ack);
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
        timer,
      });
      console.log('已设置ACK', requestId);
      this.ws!.send(JSON.stringify(envelope));
    });
  }

  private handleIncoming(ev: MessageEvent): void {
    let envelope: WsEnvelope;
    try {
      envelope = JSON.parse(String(ev.data)) as WsEnvelope;
    } catch {
      return;
    }
    if (!envelope || typeof envelope.type !== 'string' || !envelope.data)
      return;

    switch (envelope.type) {
      case MessageType.Ack:
        this.handleAck(envelope);
        break;
      case MessageType.Message:
        if (envelope.action === ActionType.Broadcast) {
          // 下行重复消息去除
          // if (this.cacheBoardList.has(envelope?.data?.messageId ?? '')) {
          //   this.cacheBoardList.delete(envelope?.data?.messageId ?? '');
          //   return;
          // }
          this.messageHandlers.forEach((h) => h(envelope));
        }
        break;
      case MessageType.System:
        this.systemHandlers.forEach((h) => h(envelope));
        break;
      case MessageType.Heartbeat:
        // 一般由客户端发起；若服务端推送 heartbeat，可在内部扩展，默认忽略
        break;
      default:
        break;
    }
  }

  private handleAck(ack: WsEnvelope): void {
    // 兼容服务端将关联 ID 放在不同字段的情况：
    // - 常见：ack.data.message_id（与本地 request_id 对齐）
    // - 兜底：ack.request_id
    // 换取消息ID
    const requestId = ack.requestId;
    if (!requestId) return;
    if (this.cacheBoardList.has(requestId)) {
      this.cacheBoardList.delete(requestId);
      this.cacheBoardList.add(ack.data?.messageId ?? '');
    }

    const pending = this.pendingAck.get(requestId);
    if (!pending) return;
    this.pendingAck.delete(requestId);
    clearTimeout(pending.timer);
    console.log('已清理ACK', requestId);

    if (ack.action === ActionType.Error) {
      const msg = ack.data.errorMsg ?? ack.data.errorCode ?? 'ACK error';
      const err = new ImWsAckError(msg, ack, ack.data.errorCode);
      this.ackErrorHandlers.forEach((h) => h(err));
      pending.reject(err);
      return;
    }
    if (ack.action === ActionType.Success) {
      pending.resolve(ack);
      return;
    }
    // 其它 ack action 仍视为成功交付，避免服务端轻微不一致导致永远挂起
    pending.resolve(ack);
  }

  private rejectAllPending(err: Error): void {
    for (const [, p] of this.pendingAck) {
      clearTimeout(p.timer);
      p.reject(err);
    }
    this.pendingAck.clear();
  }

  /**
   * ACK 超时视为链路或服务端无响应，主动断连以触发 `onclose` → 自动重连。
   */
  private closeForAckTimeout(): void {
    if (this.manualClose || !this.opts.autoReconnect) return;
    const ws = this.ws;
    if (!ws) return;
    if (
      ws.readyState === WebSocket.OPEN ||
      ws.readyState === WebSocket.CONNECTING
    ) {
      ws.close(4002, 'ack timeout');
    }
  }

  private scheduleReconnect(): void {
    if (this.manualClose || !this.opts.autoReconnect) return;
    if (
      this.opts.maxReconnectAttempts > 0 &&
      this.reconnectAttempts >= this.opts.maxReconnectAttempts
    ) {
      return;
    }

    const attempt = this.reconnectAttempts++;
    const exp = Math.min(
      this.opts.reconnectBaseDelayMs * 2 ** attempt,
      this.opts.reconnectMaxDelayMs,
    );
    const jitter =
      this.opts.reconnectJitterMs > 0
        ? Math.floor(Math.random() * (this.opts.reconnectJitterMs + 1))
        : 0;
    const delay = exp + jitter;

    setTimeout(() => {
      if (this.manualClose || this.isOpen) return;
      this.connect().catch(() => {
        /* onclose 会再次 scheduleReconnect */
      });
    }, delay);
  }
}
